/* eslint-disable react-refresh/only-export-components, react-hooks/preserve-manual-memoization */
import { ConfirmationResult, UserCredential } from 'firebase/auth'
import { Formik, FormikHelpers } from 'formik'
import { Modal, VStack, useToast } from 'native-base'
import { InterfaceToastProps } from 'native-base/lib/typescript/components/composites/Toast'
import React, { useCallback, useContext, useMemo, useRef, useState } from 'react'
import * as yup from 'yup'

import { useLazyQuery, useMutation } from '@apollo/client'
import client from '../../apolloClient'
import { AppContext } from '../../context/AppContext'
import { AuthContext } from '../../context/AuthContext'
import {
  IAdjustGeopinToast,
  IEditAddressProps,
  IToggleMapModalToastProps,
  ToggleModalFuncProps,
} from '../../context/OverlayContext'
import {
  AddressAttribute,
  AGREE_TO_TERMS_AND_CONDITIONS_CWA,
  EDIT_ORDER_CWA,
  GET_ORDER_BY_TRACKING_ID_CWA,
  NewAddressInput,
  Order,
  OrderServiceType,
  PropertyType,
  UPDATE_CLIENT_ADDRESS_INSTRUCTIONS_FOR_ORDER,
  VERIFY_PHONE_RELATED_TO_ORDER,
} from '@consumer/graphql'
import { createCanonicalContext, decorateCanonical, emitCanonical } from '@consumer/shared'
import { trackEvent } from '../../utils/analytics-v2'
import '../../utils/canonicalLog'
import { signInWithPhoneNumber } from '../../utils/firebase'
import { logError } from '../../utils/monitorClient'
import { PermanentAddressUpdateModalContent } from '../PermanentAddressUpdate/PermanentAddressUpdateModalContent'
import { ToastAlert } from './Common/ToastAlert'
import { ErrorModalContent } from './Error/ErrorModalContent'
import { PhoneNumberMismatchModalContent } from './Error/PhoneNumberMismatchModalContent'
import { AddressInputGroupType, addressFieldsSchema } from './Form/AddressForm'
import { AddressFormModalContent } from './Form/AddressFormModalContent'
import { InstructionsFormType, instructionsFormSchema } from './Form/InstructionsForm'
import { InstructionsFormModalContent } from './Form/InstructionsFormModalContent'
import { PREFERRED_LOCATIONS } from './Form/preferredLocations'
import { PhoneFormType, phoneFormSchema } from './Phone/PhoneInput'
import { PhoneModalContent } from './Phone/PhoneModalContent'
import { SubmittingModalContent } from './Submitting/SubmittingModalContent'
import { tcFormSchema } from './TermAndConditions/TermsAndConditions'
import { VerificationFormType, verificationFormSchema } from './Verification/VerificationInput'
import { VerificationModalContent } from './Verification/VerificationModalContent'

const normalizePhone = (phoneNumberString: string) => {
  // TO DO: Add normalization function based on region
  return '+' + phoneNumberString.replace(/[^\d]/g, '')
}

export enum Steps {
  Instructions = 0,
  Phone = 1,
  Verification = 2,
  Submitting = 3,
  PhoneNumberNotMatched = 4,
  Error = 5,
  EditAddress = 6,
  PermanentAddressUpdate = 7,
}

interface StepConfigOptions {
  step: Steps
  includeTermsAndConditions?: boolean
  reroutableZipCodes?: string[]
}
interface StepConfig {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  validationSchema: yup.ObjectSchema<any> | null
  validateOnChange: boolean
}

/** Get validation schema and config for the given modal step. */
function getStepConfig({ step, includeTermsAndConditions, reroutableZipCodes }: StepConfigOptions): StepConfig {
  switch (step) {
    case Steps.Instructions:
      return {
        validationSchema: includeTermsAndConditions
          ? tcFormSchema.clone().concat(instructionsFormSchema)
          : instructionsFormSchema,
        validateOnChange: true,
      }
    case Steps.Phone:
      return { validationSchema: phoneFormSchema, validateOnChange: false }
    case Steps.Verification:
      return { validationSchema: verificationFormSchema, validateOnChange: false }
    case Steps.Submitting:
      return { validationSchema: null, validateOnChange: false }
    case Steps.PhoneNumberNotMatched:
      return { validationSchema: null, validateOnChange: false }
    case Steps.Error:
      return { validationSchema: null, validateOnChange: false }
    case Steps.EditAddress:
      return {
        validationSchema: includeTermsAndConditions
          ? tcFormSchema.clone().concat(addressFieldsSchema(reroutableZipCodes))
          : addressFieldsSchema(reroutableZipCodes),
        validateOnChange: true,
      }
    case Steps.PermanentAddressUpdate:
      return { validationSchema: null, validateOnChange: true }
    default:
      return { validationSchema: null, validateOnChange: false }
  }
}

export interface ICloseModalOptions {
  clearAddressFields?: boolean
  clearGeocode?: boolean
  showToast?: boolean
  toastProps?: InterfaceToastProps
}

export interface StepContentProps {
  step: number
  setStep: (step: number) => void | React.Dispatch<React.SetStateAction<number>>
  order: Order
  closeModal: ({ clearAddressFields, clearGeocode, showToast, toastProps }: ICloseModalOptions) => void
  resendAuth: () => void
  resendLoading: boolean
  disableSubmit: boolean
  disableResend: boolean
  phoneModalProps?: ModalCustomProps
  verificationModalProps?: ModalCustomProps
  termsAndConditionsAction?: string
  submitModalProps?: ModalCustomProps
  authLoading?: boolean
  toggleEditAddress: ({ clearAddressFields, locationUpdate, useLocationUpdate, toggleMap }: IEditAddressProps) => void
  setOrder: (order: Order) => void
  handleSetAdjustGeopinToast: (toast: IAdjustGeopinToast) => void
  toggleMapModal: ({ showToastAfterClose, toastProps }: IToggleMapModalToastProps) => void
  useUpdateNormalizedAddress: boolean
  setUseUpdateNormalizedAddress: (useUpdateNormalizedAddress: boolean) => void
  includeTermsAndConditions: boolean
}

/**
 * Render the main content of the modal based on the current step.
 */
const StepContent = ({
  step,
  setStep,
  order,
  closeModal,
  resendAuth,
  resendLoading,
  disableResend,
  disableSubmit,
  phoneModalProps,
  verificationModalProps,
  termsAndConditionsAction,
  submitModalProps,
  toggleEditAddress,
  setOrder,
  handleSetAdjustGeopinToast,
  toggleMapModal,
  useUpdateNormalizedAddress,
  setUseUpdateNormalizedAddress,
  includeTermsAndConditions,
}: StepContentProps) => {
  switch (step) {
    case Steps.Instructions:
      return (
        <InstructionsFormModalContent
          order={order}
          closeModal={closeModal}
          includeTermsAndConditions={includeTermsAndConditions}
        />
      )
    case Steps.Phone:
      return (
        <PhoneModalContent
          closeModal={closeModal}
          termsAndConditionsAction={termsAndConditionsAction}
          {...phoneModalProps}
        />
      )
    case Steps.Verification:
      return (
        <VerificationModalContent
          closeModal={closeModal}
          resendAuth={resendAuth}
          resendLoading={resendLoading}
          disableSubmit={disableSubmit}
          disableResend={disableResend}
          {...verificationModalProps}
        />
      )
    case Steps.Submitting:
      return <SubmittingModalContent {...submitModalProps} />
    case Steps.PhoneNumberNotMatched:
      return <PhoneNumberMismatchModalContent closeModal={closeModal} onSubmit={() => setStep(Steps.Phone)} />
    case Steps.Error:
      return <ErrorModalContent closeModal={closeModal} onSubmit={() => setStep(Steps.Phone)} />
    case Steps.EditAddress:
      return (
        <AddressFormModalContent
          order={order}
          closeModal={closeModal}
          includeTermsAndConditions={includeTermsAndConditions}
        />
      )
    case Steps.PermanentAddressUpdate:
      return (
        <PermanentAddressUpdateModalContent
          order={order}
          closeModal={closeModal}
          toggleEditAddress={toggleEditAddress}
          setOrder={setOrder}
          handleSetAdjustGeopinToast={handleSetAdjustGeopinToast}
          toggleMapModal={toggleMapModal}
          useUpdateNormalizedAddress={useUpdateNormalizedAddress}
          setUseUpdateNormalizedAddress={setUseUpdateNormalizedAddress}
          includeTermsAndConditions={includeTermsAndConditions}
        />
      )
    default:
      return <div>Not Found</div>
  }
}

export type ModalFormType = InstructionsFormType & PhoneFormType & VerificationFormType & AddressInputGroupType

export interface InstructionsModalProps {
  order: Order
  isOpen: boolean
  setIsOpen: (step: boolean) => void | React.Dispatch<React.SetStateAction<boolean>>
  activeStep: number
  setActiveStep: (step: number) => void | React.Dispatch<React.SetStateAction<number>>
  phoneModalProps?: ModalCustomProps
  verificationModalProps?: ModalCustomProps
  termsAndConditionsAction?: string
  submitModalProps?: ModalCustomProps
  auth_flow?: ToggleModalFuncProps['auth_flow']
  // passing handlers and related values directly to avoid circular reference issues
  // InstructionsModal needs to remain a top level component due it being used for auth flow
  geocodeUpdate: { latitude: number; longitude: number }
  useLocationUpdate: boolean
  handleSetGeocodeUpdate: (geocode: { latitude: number; longitude: number }) => void
  setUseLocationUpdate: (useLocation: boolean) => void
  clearAddressFields: boolean
  handleClearAddressFields: (clear: boolean) => void
  toggleEditAddress: ({ clearAddressFields, locationUpdate, useLocationUpdate, toggleMap }: IEditAddressProps) => void
  handleSetAdjustGeopinToast: (toast: IAdjustGeopinToast) => void
  toggleMapModal: ({ showToastAfterClose, toastProps }: IToggleMapModalToastProps) => void
  useUpdateNormalizedAddress: boolean
  setUseUpdateNormalizedAddress: (useUpdateNormalizedAddress: boolean) => void
}

interface ModalCustomProps {
  modalHeaderText?: string
  modalBodyText?: string
  cancelText?: string
}

export const sanitizeEditAddressInput = (input: Partial<ModalFormType>, serviceType: OrderServiceType) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { phoneNumber, acceptedTerms, confirmationCode, ...editAddressInputFields } = input

  return {
    ...editAddressInputFields,
    ...(serviceType === OrderServiceType.Pickup
      ? {
          pickupInstructions: input.pickupInstructions ?? '',
          instructions: undefined,
        }
      : {
          instructions: input.instructions ?? '',
          pickupInstructions: undefined,
        }),
    securityCode:
      input.attributes?.find((attr: AddressAttribute) => attr === AddressAttribute.UseCodeToAccess) ||
      input?.securityCode
        ? input.securityCode
        : '',
  }
}

/** Modal for editing delivery instructions, address, and phone verification. */
export default function InstructionsModal({
  order,
  isOpen,
  setIsOpen,
  activeStep,
  setActiveStep,
  phoneModalProps,
  verificationModalProps,
  termsAndConditionsAction,
  submitModalProps,
  auth_flow = 'general',
  geocodeUpdate,
  useLocationUpdate,
  handleSetGeocodeUpdate,
  setUseLocationUpdate,
  clearAddressFields,
  handleClearAddressFields,
  toggleEditAddress,
  handleSetAdjustGeopinToast,
  toggleMapModal,
  useUpdateNormalizedAddress,
  setUseUpdateNormalizedAddress,
}: InstructionsModalProps) {
  const toast = useToast()
  const { firebaseUser, user, reloadUser } = useContext(AuthContext)
  const { setOrder } = useContext(AppContext)
  // Setup the phone validation
  const [recaptchaWrapperRef, setRecaptchaWrapperRef] = useState<HTMLDivElement | null>(null)
  const [resendLoading, setResendLoading] = useState(false)
  const [normalizedPhoneNumber, setNormalizedPhoneNumber] = useState<string | null>(null)
  const [submitVerifyPhoneLoading, setSubmitVerifyPhoneLoading] = useState(false)
  const [confirmation, setConfirmation] = useState<ConfirmationResult>()
  const [verifyAttemptCount, setVerifyAttemptCount] = useState(0)
  const [disableSubmit, setDisableSubmit] = useState(false)
  const [disableResend, setDisableResend] = useState(false)
  const loggedInButNotAgreedToTermsAndConditions = !!firebaseUser && !user?.agreedToTermsAndConditions
  const clientId = order?.clientId ?? undefined

  const authFlowOpIdRef = useRef<string>('')
  const authFlowStartMsRef = useRef<number>(0)
  const instructionsSaveOpIdRef = useRef<string>('')
  const instructionsSaveStartMsRef = useRef<number>(0)

  const [verifyPhoneRelatedToOrder] = useLazyQuery<{
    isOrderAssociatedWithPhoneNumber: boolean
  }>(VERIFY_PHONE_RELATED_TO_ORDER, {
    fetchPolicy: 'network-only',
  })
  const [agreedToTermsAndConditions] = useMutation(AGREE_TO_TERMS_AND_CONDITIONS_CWA, { onCompleted: reloadUser })
  const [updateInstructions] = useMutation(UPDATE_CLIENT_ADDRESS_INSTRUCTIONS_FOR_ORDER, {
    refetchQueries: [GET_ORDER_BY_TRACKING_ID_CWA],
    onCompleted: () => {
      const sOpId = instructionsSaveOpIdRef.current
      if (sOpId) {
        decorateCanonical(sOpId, { mutationDurationMs: Date.now() - instructionsSaveStartMsRef.current })
        emitCanonical(sOpId, 'success')
        instructionsSaveOpIdRef.current = ''
      }
      // Submission successful, which means we have verified the phone number and that the phone number matches the order
      // Submit the terms and conditions agreement that was required to submit the instructions
      // TODO: should we do this earlier in the process?
      //   My thinking was to avoid a race condition with updating the instructions,
      //   and also to avoid adding more latency to the instructions update process
      agreedToTermsAndConditions()
      // Refetch all queries to update the UI
      // client.refetchQueries({ include: 'all' })
      // Close the modal and reset location update to default
      setIsOpen(false)
      setUseLocationUpdate(false)
      handleSetGeocodeUpdate({
        latitude: 0,
        longitude: 0,
      })
      // Show a success toast
      trackEvent('add_instructions_success', {
        features: 'cwa_map_view',
        veho_client_id: clientId,
      })
      toast.show({
        placement: 'top',
        render: ({ id }) => (
          <ToastAlert
            id={id}
            title={'Instructions updated!'}
            description={'Added your instructions to this delivery'}
            status={'success'}
            variant={'subtle'}
            isClosable={false}
            closeToast={toast.close}
          />
        ),
      })
    },
    onError: error => {
      const sOpId = instructionsSaveOpIdRef.current
      if (sOpId) {
        decorateCanonical(sOpId, {
          mutationDurationMs: Date.now() - instructionsSaveStartMsRef.current,
          error: error?.message ?? 'unknown',
        })
        emitCanonical(sOpId, 'failure')
        instructionsSaveOpIdRef.current = ''
      }
      if (error.message === 'Authorized consumer does not match order consumer') {
        // Submission failed due to phone number not matching
        trackEvent('add_instructions_error', {
          error_reason: 'mismatched_phone',
          features: 'cwa_map_view',
          veho_client_id: clientId,
        })
        setActiveStep(Steps.PhoneNumberNotMatched)
      } else {
        // Submission failed due to an unknown error
        trackEvent('add_instructions_error', {
          error_reason: 'unknown',
          features: 'cwa_map_view',
          veho_client_id: clientId,
        })
        setActiveStep(Steps.Error)
      }
    },
  })
  const [editOrder] = useMutation(EDIT_ORDER_CWA, {
    fetchPolicy: 'network-only',
    onCompleted: data => {
      if (data?.editOrder?.address) {
        setOrder(data?.editOrder as Order)
        toast.show({
          placement: 'top',
          render: ({ id }) => (
            <ToastAlert
              id={id}
              title={'Address updated!'}
              description={'Updated your address for this delivery'}
              status={'success'}
              variant={'subtle'}
              isClosable={false}
              closeToast={toast.close}
            />
          ),
        })
      }
      // Close the modal
      setIsOpen(false)
      handleClearAddressFields(false)
      // Show a success toast
      trackEvent('edit_address_success', {
        features: 'cwa_map_view',
        veho_client_id: clientId,
      })
    },
    onError: error => {
      logError(error?.message, { error })
      trackEvent('edit_address_error', {
        features: 'cwa_map_view',
        error_reason: 'unknown',
        veho_client_id: clientId,
      })
      setActiveStep(Steps.Error)
    },
  })

  /**
   * Handle the submission of the phone number form. Normalizes the phone number and sends it to Firebase for verification.
   */
  const onPhoneFormSubmit = useCallback(
    async ({ phoneNumber, setError }: { phoneNumber: string; setError: (msg: string) => void }) => {
      // Emit any in-flight auth context as 'abandoned' before starting a new one.
      // This prevents concurrent rapid-submit calls from stomping each other's opId.
      if (authFlowOpIdRef.current) {
        emitCanonical(authFlowOpIdRef.current, 'abandoned')
      }

      const opId = crypto.randomUUID()
      authFlowOpIdRef.current = opId
      authFlowStartMsRef.current = Date.now()
      createCanonicalContext('CanonicalAuthFlow', opId, {
        authFlow: auth_flow,
        platform: 'web',
      })

      try {
        trackEvent('get_authentication_code', {
          auth_flow: auth_flow,
          features: 'cwa_map_view',
          veho_client_id: clientId,
        })
        const normalizedPhone = normalizePhone(phoneNumber)

        if (recaptchaWrapperRef) {
          // Save the normalized phone number incase we need to resend the verification code
          setNormalizedPhoneNumber(normalizedPhone)
          const verifyPhoneData = await verifyPhoneRelatedToOrder({
            variables: {
              orderId: order?.orderId,
              phone: normalizedPhone,
            },
          })

          if (!verifyPhoneData.data?.isOrderAssociatedWithPhoneNumber) {
            decorateCanonical(opId, {
              errorStage: 'otpRequest',
              error: 'phone_mismatch',
              otpRequestDurationMs: Date.now() - authFlowStartMsRef.current,
            })
            emitCanonical(opId, 'failure')
            trackEvent('add_instructions_error', {
              error_reason: 'mismatched_phone',
              features: 'cwa_map_view',
              veho_client_id: clientId,
            })
            setActiveStep(Steps.PhoneNumberNotMatched)
            return false
          }
          // Firebase auth requires phone number in format +1 5557770160
          const firebaseConfirmation = await signInWithPhoneNumber(normalizedPhone, recaptchaWrapperRef)
          if (firebaseConfirmation) {
            decorateCanonical(opId, { otpRequestDurationMs: Date.now() - authFlowStartMsRef.current })
            trackEvent('get_authentication_code_success', {
              auth_flow: auth_flow,
              features: 'cwa_map_view',
              veho_client_id: clientId,
            })
            setConfirmation(firebaseConfirmation)
          }
        }
      } catch (e: unknown) {
        decorateCanonical(opId, {
          errorStage: 'otpRequest',
          error: e instanceof Error ? e.message : 'unknown',
          otpRequestDurationMs: Date.now() - authFlowStartMsRef.current,
        })
        emitCanonical(opId, 'failure')
        if (e instanceof Error) {
          if (e.message.includes('reCAPTCHA')) {
            trackEvent('get_authentication_code_error', {
              auth_flow: auth_flow,
              error_reason: 'verification_failed',
              features: 'cwa_map_view',
              veho_client_id: clientId,
            })
            setError('Phone verification failed. Please reload the page and try again.')
          } else {
            trackEvent('get_authentication_code_error', {
              auth_flow: auth_flow,
              error_reason: 'unknown',
              features: 'cwa_map_view',
              veho_client_id: clientId,
            })
            setError('An error occurred. Please try again.')
          }
        }
        return false
      }
      return true
    },
    [recaptchaWrapperRef, order?.orderId, setActiveStep, verifyPhoneRelatedToOrder, auth_flow, clientId]
  )

  /**
   * Handle the submission of the verification code form. Sends the code to Firebase for verification.
   * If the code is correct, the phone number is verified and the page will be reloaded with the authenticated user.
   * If the code is incorrect, the user will be prompted to try again.
   */
  const onVerifyPhoneSubmit = useCallback(
    async ({ values, setError }: { values: Partial<ModalFormType>; setError: (msg: string) => void }) => {
      const {
        confirmationCode,
        type,
        attributes,
        instructions,
        preferredLocation,
        pickupInstructions,
        preferredPickupLocation,
        securityCode,
      } = values
      if (confirmation) {
        const opId = authFlowOpIdRef.current
        const verifyStartMs = Date.now()
        trackEvent('submit_verification_code', {
          auth_flow: auth_flow,
          features: 'cwa_map_view',
          veho_client_id: clientId,
        })
        setSubmitVerifyPhoneLoading(true)

        let confirmRes: UserCredential
        try {
          confirmRes = await confirmation.confirm(confirmationCode!)
          if (confirmRes) {
            decorateCanonical(opId, {
              verifyDurationMs: Date.now() - verifyStartMs,
              attemptCount: verifyAttemptCount + 1,
            })
            emitCanonical(opId, 'success')
            trackEvent('submit_verification_code_success', {
              auth_flow: auth_flow,
              features: 'cwa_map_view',
              veho_client_id: clientId,
            })

            if (auth_flow === 'instructions') {
              const sOpId = crypto.randomUUID()
              instructionsSaveOpIdRef.current = sOpId
              instructionsSaveStartMsRef.current = Date.now()
              createCanonicalContext('CanonicalInstructionsSave', sOpId, {
                platform: 'web',
                propertyType: values.type ?? null,
                // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
                hasAddressInstructions: !!(values.instructions || values.securityCode || values.preferredLocation),
              })
              updateInstructions({
                errorPolicy: 'none',
                notifyOnNetworkStatusChange: true,
                variables: {
                  orderId: order?.orderId,
                  updateInstructionsInput: {
                    attributes,
                    instructions: instructions ?? undefined,
                    pickupInstructions: instructions == null ? undefined : pickupInstructions,
                    preferredLocation: preferredLocation === PREFERRED_LOCATIONS.NotSet ? undefined : preferredLocation,
                    preferredPickupLocation,
                    securityCode,
                    type,
                  },
                },
              })
            } else {
              // refetch when not instructions to update order and agree to T&C
              client.refetchQueries({ include: 'all' })
              agreedToTermsAndConditions()
            }
          }
        } catch (e: unknown) {
          setSubmitVerifyPhoneLoading(false)
          setVerifyAttemptCount(verifyAttemptCount + 1)
          if (verifyAttemptCount >= 3) {
            decorateCanonical(opId, {
              error: 'too_many_attempts',
              verifyDurationMs: Date.now() - verifyStartMs,
              attemptCount: verifyAttemptCount + 1,
            })
            emitCanonical(opId, 'failure')
            setDisableSubmit(true)
            setError('Too many attempts. Please try again later.')
            trackEvent('submit_verification_code_error', {
              auth_flow: auth_flow,
              error_reason: 'too_many_attempts',
              features: 'cwa_map_view',
              veho_client_id: clientId,
            })
          } else {
            decorateCanonical(opId, {
              errorStage: 'verify',
              error: e instanceof Error ? e.message : 'unknown',
              verifyDurationMs: Date.now() - verifyStartMs,
            })
            if (e instanceof Error) {
              if (e?.message?.includes('auth/invalid-verification-code')) {
                trackEvent('submit_verification_code_error', {
                  auth_flow: auth_flow,
                  error_reason: 'code_mismatch',
                  features: 'cwa_map_view',
                  veho_client_id: clientId,
                })
                setError('Verification code does not match.')
              } else if (e?.message?.includes('auth/code-expired')) {
                trackEvent('submit_verification_code_error', {
                  auth_flow: auth_flow,
                  error_reason: 'code_expired',
                  features: 'cwa_map_view',
                  veho_client_id: clientId,
                })
                setError('Verification code has expired.')
              } else {
                setError('An error occurred. Please try again.')
              }
            }
          }

          return false
        }
      }
      return true
    },
    [
      confirmation,
      verifyAttemptCount,
      setSubmitVerifyPhoneLoading,
      order,
      updateInstructions,
      auth_flow,
      agreedToTermsAndConditions,
      clientId,
    ]
  )

  /**
   * Resend the authentication code to the user's phone number.
   * Only allows resending once per session (due to a limitation with reloading the ReCAPTCHA widget).
   */
  const resendAuth = useCallback(async () => {
    if (!submitVerifyPhoneLoading && !disableResend) {
      setResendLoading(true)
      if (normalizedPhoneNumber && recaptchaWrapperRef) {
        try {
          trackEvent('get_authentication_code', { auth_flow: auth_flow })
          const firebaseConfirmation = await signInWithPhoneNumber(normalizedPhoneNumber, recaptchaWrapperRef)

          if (firebaseConfirmation) {
            trackEvent('get_authentication_code_success', {
              auth_flow: auth_flow,
              features: 'cwa_map_view',
              veho_client_id: clientId,
            })
            setConfirmation(firebaseConfirmation)
          }
        } catch {
          trackEvent('get_authentication_code_error', {
            auth_flow: auth_flow,
            error_reason: 'unknown',
            features: 'cwa_map_view',
            veho_client_id: clientId,
          })
          // TODO: Do we want to display an error message?
        } finally {
          setDisableResend(true)
        }
      }
      setResendLoading(false)
    }
  }, [normalizedPhoneNumber, submitVerifyPhoneLoading, disableResend, recaptchaWrapperRef, auth_flow, clientId])

  /**
   * Submit the address attributes edits (e.g. instructions/preferredLocation) or address changes for reroutes
   * Can also send location updates for geocode corrections by the consumer
   */
  const handleEditAddressSubmit = useCallback(
    async ({ values }: { values: Partial<ModalFormType> }) => {
      // Agree to terms and conditions if the value is set (meaning the user has checked the box)
      if (values.acceptedTerms) {
        await agreedToTermsAndConditions()
      }
      const sanitizedValues: NewAddressInput = sanitizeEditAddressInput(values, order?.serviceType) as NewAddressInput
      const updatedOrder: NewAddressInput = {
        ...sanitizedValues,
        ...(useLocationUpdate ? { location: { lat: geocodeUpdate.latitude, lng: geocodeUpdate.longitude } } : {}),
        userServedGeocodeCorrectionFlag: true,
        // default to true to avoid user being shown PermanentAddressUpdate modal
        ...(order?.addressCorrectionAllowed ? { consumerSpecifiedAddressNormalization: true } : {}),
      }

      try {
        editOrder({
          errorPolicy: 'none',
          notifyOnNetworkStatusChange: true,
          variables: {
            orderId: order?.orderId,
            addressInput: updatedOrder,
          },
        })
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (error: any) {
        const errorDescription: string = error?.message?.includes('Order is past cut-off for address changes')
          ? "Sorry, we can't make changes to your delivery address after 6am the day of your delivery. You can still update your delivery instructions at any time!"
          : 'Sorry, something went wrong updating your delivery address.'

        await client.refetchQueries({ include: 'all' })
        toast.show({
          placement: 'top',
          render: ({ id }) => (
            <ToastAlert
              id={id}
              title={'Unable to Update Delivery Address'}
              description={errorDescription}
              status={'error'}
              variant={'subtle'}
              isClosable={false}
              closeToast={toast.close}
            />
          ),
        })
        return false
      }
      return true
    },
    [
      editOrder,
      order?.orderId,
      order?.serviceType,
      toast,
      geocodeUpdate,
      useLocationUpdate,
      order?.addressCorrectionAllowed,
      agreedToTermsAndConditions,
    ]
  )

  const handleEditInstructions = useCallback(
    ({ values }: { values: Partial<ModalFormType> }) => {
      const {
        type,
        attributes,
        instructions,
        preferredLocation,
        pickupInstructions,
        preferredPickupLocation,
        securityCode,
      } = values

      // Emit any in-flight instructions save as 'abandoned' before starting a new one.
      if (instructionsSaveOpIdRef.current) {
        emitCanonical(instructionsSaveOpIdRef.current, 'abandoned')
      }

      const sOpId = crypto.randomUUID()
      instructionsSaveOpIdRef.current = sOpId
      instructionsSaveStartMsRef.current = Date.now()
      createCanonicalContext('CanonicalInstructionsSave', sOpId, {
        platform: 'web',
        propertyType: type ?? null,
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
        hasAddressInstructions: !!(instructions || securityCode || preferredLocation),
      })

      try {
        updateInstructions({
          errorPolicy: 'none',
          notifyOnNetworkStatusChange: true,
          variables: {
            orderId: order?.orderId,
            updateInstructionsInput: {
              attributes,
              instructions: instructions ?? undefined,
              pickupInstructions: instructions == null ? undefined : pickupInstructions,
              preferredLocation: preferredLocation === PREFERRED_LOCATIONS.NotSet ? undefined : preferredLocation,
              preferredPickupLocation,
              securityCode,
              type,
            },
          },
        })
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (err: any) {
        // updateInstructions is an Apollo mutation and does not throw synchronously for
        // network/GraphQL errors (those go to onError). This catch handles rare synchronous
        // pre-flight errors. Emit the context so it is never leaked.
        emitCanonical(sOpId, 'failure')
        instructionsSaveOpIdRef.current = ''

        if (err?.message === 'Authorized consumer does not match order consumer') {
          // Submission failed due to phone number not matching
          trackEvent('add_instructions_error', {
            error_reason: 'mismatched_phone',
            features: 'cwa_map_view',
            veho_client_id: clientId,
          })
          setActiveStep(Steps.PhoneNumberNotMatched)
        } else {
          // Submission failed due to an unknown error
          trackEvent('add_instructions_error', {
            error_reason: 'unknown',
            features: 'cwa_map_view',
            veho_client_id: clientId,
          })
          setActiveStep(Steps.Error)
        }
      }
    },
    [order?.orderId, setActiveStep, updateInstructions, clientId]
  )

  // Setup the initial values for the form
  const { validationSchema, validateOnChange } = getStepConfig({
    step: activeStep,
    includeTermsAndConditions: loggedInButNotAgreedToTermsAndConditions,
    reroutableZipCodes: order?.reroutableZipCodes,
  })

  const filteredAttributes: AddressAttribute[] = useMemo(
    () => order?.address?.attributes?.filter(Boolean).map(attr => attr!) ?? [],
    [order?.address?.attributes]
  )
  const initialValues: Partial<ModalFormType> = useMemo(() => {
    // Since these two fields are optional and hidden by default, we want to have the concept of "NotSet".
    // If the user has never opened the other instructions section, they have not made any selections for these fields
    // so we should not update the values in the database.
    const conditionalFields =
      order?.serviceType === 'pickup'
        ? {
            preferredPickupLocation: order?.address?.preferredPickupLocation ?? PREFERRED_LOCATIONS.NotSet,
            pickupInstructions: order?.address?.pickupInstructions ?? null,
          }
        : {
            preferredLocation: order?.address?.preferredLocation ?? PREFERRED_LOCATIONS.NotSet,
            instructions: order?.address?.instructions ?? null,
          }
    return {
      type: order?.address?.type ?? PropertyType.House,
      attributes: filteredAttributes,
      securityCode: order?.address?.securityCode ?? '',
      phoneNumber: '',
      acceptedTerms: false,
      confirmationCode: '',
      name: order?.address?.name ?? '',
      street: clearAddressFields ? '' : (order?.address?.street ?? ''),
      apartment: clearAddressFields ? '' : (order?.address?.apartment ?? ''),
      city: clearAddressFields ? '' : (order?.address?.city ?? ''),
      state: order?.address?.state ?? '',
      zipCode: order?.address?.zipCode ?? '',
      ...conditionalFields,
    }
  }, [order?.address, order?.serviceType, filteredAttributes, clearAddressFields])
  /**
   * Close the instructions modal.
   */

  const _closeModal = useCallback(
    (options?: ICloseModalOptions) => {
      const { clearAddressFields = false, clearGeocode = false, showToast = false, toastProps } = options ?? {}

      // Emit any open canonical contexts as 'abandoned' on modal close.
      // This covers the case where the user dismisses the modal mid-flow
      // (e.g. closes after entering phone but before verifying the OTP).
      if (authFlowOpIdRef.current) {
        emitCanonical(authFlowOpIdRef.current, 'abandoned')
        authFlowOpIdRef.current = ''
      }
      if (instructionsSaveOpIdRef.current) {
        emitCanonical(instructionsSaveOpIdRef.current, 'abandoned')
        instructionsSaveOpIdRef.current = ''
      }

      setIsOpen(false)

      if (clearAddressFields) {
        handleClearAddressFields(false)
      }
      if (clearGeocode) {
        handleSetGeocodeUpdate({
          latitude: 0,
          longitude: 0,
        })
      }
      if (showToast) {
        toast.show({
          ...toastProps,
        })
      }
    },
    [handleClearAddressFields, setIsOpen, handleSetGeocodeUpdate, toast]
  )

  /**
   * Handle the submission of the form based on the current step.
   * Will do step-specific actions, then progress the submission flow forward if there are no errors.
   * @param values Form values.
   * @param formikHelpers Formik helpers functions.
   */
  const _handleSubmit = async (
    values: Partial<ModalFormType>,
    formikHelpers: FormikHelpers<Partial<ModalFormType>>
  ) => {
    let moveToNextStep = true
    switch (activeStep) {
      case Steps.Phone:
        moveToNextStep = await onPhoneFormSubmit({
          phoneNumber: values.phoneNumber!,
          setError: msg => formikHelpers.setErrors({ phoneNumber: msg }),
        })
        break
      case Steps.Verification:
        moveToNextStep = await onVerifyPhoneSubmit({
          values,
          setError: msg => formikHelpers.setErrors({ confirmationCode: msg }),
        })
        break
      case Steps.Instructions:
        trackEvent('add_instructions', { features: 'cwa_map_view' })
        break
      case Steps.EditAddress:
        moveToNextStep = await handleEditAddressSubmit({ values })
        break
      default:
        break
    }

    if (moveToNextStep) {
      if (auth_flow === 'edit_address' && activeStep === Steps.Verification) {
        // refetch to update address fields and close modal
        client.refetchQueries({ include: 'all' })
        setIsOpen(false)
      } else if (auth_flow === 'permanent_address_update' && activeStep === Steps.Verification) {
        // refetch to update address fields and close modal
        client.refetchQueries({ include: 'all' })
        setIsOpen(false)
      } else if (auth_flow === 'edit_address' && activeStep === Steps.EditAddress) {
        // set modal step to submit - handleEditAddressSubmit will close modal in onComplete
        setActiveStep(Steps.Submitting)
      } else if (auth_flow === 'permanent_address_update' && activeStep === Steps.PermanentAddressUpdate) {
        // set modal step to submit - handleEditAddressSubmit will close modal in onComplete
        setActiveStep(Steps.Submitting)
      } else if (auth_flow === 'general' && activeStep === Steps.Verification) {
        // general login flow should close modal after auth
        client.refetchQueries({ include: 'all' })
        setIsOpen(false)
      } else if (auth_flow === 'instructions' && (firebaseUser || (order?.address?.street && order?.address?.name))) {
        // if user is authed - directly call editInstructions mutation
        handleEditInstructions({ values })
        setActiveStep(Steps.Submitting)
      } else {
        setActiveStep(activeStep + 1)
      }
      formikHelpers.setTouched({})
    }
    formikHelpers.setSubmitting(false)
  }

  return (
    <VStack space={3} alignItems={'center'}>
      <Modal isOpen={isOpen} onClose={_closeModal} size={'xl'}>
        <Formik
          initialValues={initialValues}
          enableReinitialize={true}
          validationSchema={validationSchema}
          validateOnMount={true}
          validateOnChange={validateOnChange}
          onSubmit={_handleSubmit}
        >
          <StepContent
            step={activeStep}
            setStep={setActiveStep}
            order={order}
            closeModal={_closeModal}
            resendAuth={resendAuth}
            resendLoading={resendLoading}
            disableSubmit={disableSubmit}
            disableResend={disableResend}
            phoneModalProps={phoneModalProps}
            verificationModalProps={verificationModalProps}
            termsAndConditionsAction={termsAndConditionsAction}
            submitModalProps={submitModalProps}
            toggleEditAddress={toggleEditAddress}
            setOrder={setOrder}
            handleSetAdjustGeopinToast={handleSetAdjustGeopinToast}
            toggleMapModal={toggleMapModal}
            useUpdateNormalizedAddress={useUpdateNormalizedAddress}
            setUseUpdateNormalizedAddress={setUseUpdateNormalizedAddress}
            includeTermsAndConditions={loggedInButNotAgreedToTermsAndConditions}
          />
        </Formik>
      </Modal>
      <div ref={ref => setRecaptchaWrapperRef(ref)}>
        <div id="recaptcha-container"></div>
      </div>
    </VStack>
  )
}
